import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Home, Key, FileText, Shield, LogOut, User, Building2, CheckCircle } from "lucide-react";

const Dashboard = () => {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user data was passed from AuthCallback, we're good
    if (location.state?.user) return;
    
    if (!loading && !user) {
      navigate("/");
    }
  }, [user, loading, navigate, location.state]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="dashboard-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C04000] mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  const displayUser = location.state?.user || user;

  if (!displayUser) {
    return null;
  }

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#C04000] rounded-lg flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              MyDoor360
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
                {displayUser.picture ? (
                  <img src={displayUser.picture} alt={displayUser.name} className="w-8 h-8 rounded-full" />
                ) : (
                  <User className="w-4 h-4 text-slate-600" />
                )}
              </div>
              <span className="text-sm font-medium text-slate-700">{displayUser.name || displayUser.email}</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-slate-600 hover:text-slate-900"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Welcome Section */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Welcome, {displayUser.name || 'User'}!
          </h1>
          <p className="text-slate-600">
            Your blockchain-powered real estate journey starts here.
          </p>
        </div>

        {/* Onboarding Checklist */}
        <Card className="mb-10 border-[#C04000]/20 bg-[#FFF3ED]" data-testid="onboarding-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <CheckCircle className="w-5 h-5 text-[#C04000]" />
              Complete Your Profile
            </CardTitle>
            <CardDescription>
              Finish these steps to unlock all features and start touring properties
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-slate-700">
                <div className="w-6 h-6 rounded-full bg-[#C04000] flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <span className="line-through text-slate-500">Create Account</span>
              </div>
              <div className="flex items-center gap-3 text-slate-700">
                <div className="w-6 h-6 rounded-full border-2 border-slate-300 flex items-center justify-center">
                  <span className="text-xs font-bold text-slate-400">2</span>
                </div>
                <span>Complete Credit Check</span>
              </div>
              <div className="flex items-center gap-3 text-slate-700">
                <div className="w-6 h-6 rounded-full border-2 border-slate-300 flex items-center justify-center">
                  <span className="text-xs font-bold text-slate-400">3</span>
                </div>
                <span>Complete Background Check</span>
              </div>
              <div className="flex items-center gap-3 text-slate-700">
                <div className="w-6 h-6 rounded-full border-2 border-slate-300 flex items-center justify-center">
                  <span className="text-xs font-bold text-slate-400">4</span>
                </div>
                <span>Choose Subscription Plan</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer" data-testid="browse-properties-card">
            <CardContent className="pt-6">
              <div className="w-12 h-12 bg-[#C04000]/10 rounded-lg flex items-center justify-center mb-4">
                <Building2 className="w-6 h-6 text-[#C04000]" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Browse Properties
              </h3>
              <p className="text-sm text-slate-600">
                Explore available listings for sale or rent
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer" data-testid="my-tours-card">
            <CardContent className="pt-6">
              <div className="w-12 h-12 bg-[#C04000]/10 rounded-lg flex items-center justify-center mb-4">
                <Key className="w-6 h-6 text-[#C04000]" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                My Tours
              </h3>
              <p className="text-sm text-slate-600">
                Schedule and manage property tours
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer" data-testid="my-documents-card">
            <CardContent className="pt-6">
              <div className="w-12 h-12 bg-[#C04000]/10 rounded-lg flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-[#C04000]" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Documents
              </h3>
              <p className="text-sm text-slate-600">
                View your blockchain-verified documents
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer" data-testid="verification-card">
            <CardContent className="pt-6">
              <div className="w-12 h-12 bg-[#C04000]/10 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-[#C04000]" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Verification
              </h3>
              <p className="text-sm text-slate-600">
                Complete credit & background checks
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
